import { apiService } from './api';
import { API_BASE_URL as API_BASE } from '../config/api';
import {
  GRNDTO,
  GRNListDTO,
  GRNApprovalStatusDTO,
  CreateGRNRequest,
  UpdateGRNRequest,
  POForGRN,
} from '../types';
const GRN_API  = '/grn';

export const grnService = {
  // ── CRUD ──────────────────────────────────────────────────────────────────

  getGRNs: (pageNumber = 1, pageSize = 50) =>
    apiService.get<{ data: GRNListDTO[]; pagination: { total: number; page: number; pageSize: number } }>(
      GRN_API, { pageNumber, pageSize }
    ),

  getGRNById: (id: number | string) =>
    apiService.get<GRNDTO>(`${GRN_API}/${id}`),

  createGRN: (data: CreateGRNRequest) =>
    apiService.post<GRNDTO>(GRN_API, data),

  updateGRN: (id: number | string, data: UpdateGRNRequest) =>
    apiService.put<GRNDTO>(`${GRN_API}/${id}`, data),

  deleteGRN: (id: number | string) =>
    apiService.delete<boolean>(`${GRN_API}/${id}`),

  // ── Filters ───────────────────────────────────────────────────────────────

  getGRNsByStatus: (status: string, pageNumber = 1, pageSize = 50) =>
    apiService.get<{ data: GRNListDTO[]; pagination: { total: number; page: number; pageSize: number } }>(
      `${GRN_API}/status/${status}`, { pageNumber, pageSize }
    ),

  getGRNsBySupplier: (supplierId: number, pageNumber = 1, pageSize = 50) =>
    apiService.get<{ data: GRNListDTO[]; pagination: { total: number; page: number; pageSize: number } }>(
      `${GRN_API}/supplier/${supplierId}`, { pageNumber, pageSize }
    ),

  getGRNStats: () =>
    apiService.get<{ totalGRN: number; pendingApproval: number; totalItemsReceived: number; totalDefectiveItems: number }>(
      `${GRN_API}/stats`
    ),

  // ── Verify ────────────────────────────────────────────────────────────────

  verifyGRN: (id: number | string, verifiedByUserId: number) =>
    apiService.post<GRNDTO>(`${GRN_API}/${id}/verify`, { verifiedByUserId }),

  // ── Multi-level approval ──────────────────────────────────────────────────

  /** Submit GRN into the approval workflow. If no rules are configured, directly approves. */
  submitForApproval: (id: number | string, submittedByUserId: number) =>
    apiService.post<GRNApprovalStatusDTO>(`${GRN_API}/${id}/submit-for-approval`, { submittedByUserId }),

  /** Get the full approval status (levels + history) for a GRN. */
  getApprovalStatus: (id: number | string) =>
    apiService.get<GRNApprovalStatusDTO>(`${GRN_API}/${id}/approvals`),

  /** Approve one approval level. */
  approveGRNLevel: (approvalId: number, userId: number, comments?: string) =>
    apiService.post<GRNApprovalStatusDTO>(`${GRN_API}/approvals/${approvalId}/approve`, { userId, comments }),

  /** Reject one approval level. */
  rejectGRNLevel: (approvalId: number, userId: number, rejectionReason: string) =>
    apiService.post<GRNApprovalStatusDTO>(`${GRN_API}/approvals/${approvalId}/reject`, { userId, rejectionReason }),

  /** Complete a fully-approved GRN and update stock. */
  completeGRN: (id: number | string, completedByUserId: number) =>
    apiService.post<GRNDTO>(`${GRN_API}/${id}/complete`, { verifiedByUserId: completedByUserId }),

  /** Get all GRNs pending approval for a specific user. */
  getPendingApprovals: (userId: number, pageNumber = 1, pageSize = 50) =>
    apiService.get<{ data: GRNApprovalStatusDTO[]; pagination: { total: number; page: number; pageSize: number } }>(
      `${GRN_API}/pending-approvals`, { userId, pageNumber, pageSize }
    ),

  // ── WithPO support ────────────────────────────────────────────────────────

  /** Returns approved POs with pending lines for the selected supplier. */
  getApprovedPOsBySupplier: (supplierId: number) =>
    apiService.get<POForGRN[]>(`${GRN_API}/supplier/${supplierId}/approved-pos`),

  // ── PDF / Print ───────────────────────────────────────────────────────────

  /**
   * Opens the GRN PDF in a new browser tab.
   * Downloads the PDF as a blob and creates a temporary object URL so the
   * browser renders it inline rather than triggering a download prompt.
   */
  openPdfInNewTab: async (id: number | string): Promise<void> => {
    const token    = localStorage.getItem('auth_token');
    const response = await fetch(
      `${API_BASE}/grn/${id}/pdf`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error(`Failed to fetch PDF (${response.status})`);
    const blob = await response.blob();
    const url  = URL.createObjectURL(blob);
    const win  = window.open(url, '_blank');
    // Revoke the object URL after a short delay so memory is released
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
    if (!win) throw new Error('Popup blocked — please allow popups for this site.');
  },
};
